import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarCheck2, CalendarClock, AlertTriangle, Search, MapPin, Users2, Building2,
  CalendarDays, Star, Sparkles, ShieldCheck, Store, FileSpreadsheet, Info, Percent, Wallet, Target, Hourglass, UserCheck,
} from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { SectionCard } from '@/components/common/SectionCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { downloadPost } from '@/lib/download';
import { fmtInt, fmtPct, fmtEur, fmtNum, fmtDate } from '@/lib/format';

const ALL = '__ALL__';
const DAY_ABBR = { Lundi: 'Lun', Mardi: 'Mar', Mercredi: 'Mer', Jeudi: 'Jeu', Vendredi: 'Ven', Samedi: 'Sam', Dimanche: 'Dim' };
const CANAUX = ['Porte à Porte', 'Rue Paris IDF', 'Rue itinérante', 'Sites privés'];
const SORTS = [
  { value: 'score', label: 'Indice d\'opportunité' },
  { value: 'age_desc', label: 'Âge moyen (élevé → faible)' },
  { value: 'age_asc', label: 'Âge moyen (faible → élevé)' },
  { value: 'bs', label: 'Volume (BS brut/jour)' },
  { value: 'don', label: 'Don moyen' },
  { value: 'transfo', label: 'Transformation' },
];

// Next 9 bookable months from the current month (client clock).
const buildMonths = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 9; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
};

const ELIG = {
  ok: { label: 'Recommandé', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30', icon: ShieldCheck },
  jachere: { label: 'Jachère', cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30', icon: CalendarClock },
  saison: { label: 'Saison interdite', cls: 'bg-amber-500/10 text-amber-700 border-amber-500/30', icon: AlertTriangle },
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-0.5 text-[9px] uppercase tracking-tight text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" /><span className="break-words">{label}</span>
    </div>
    <p className="mt-0.5 font-display text-sm font-semibold tabular text-foreground">{value}</p>
  </div>
);

const RecruiterRow = ({ r }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-lg bg-muted/40 px-2 py-1.5" data-testid="booking-recruiter">
    <span className="flex min-w-0 items-center gap-1.5">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${r.is_active ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
      <span className="break-words text-xs font-medium text-foreground">{r.name}</span>
      {!r.is_active && <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">inactif</span>}
    </span>
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] tabular text-muted-foreground">
      <span className="font-semibold text-foreground">{fmtNum(r.bs_par_jour, 1)}<span className="font-normal text-muted-foreground">/j</span></span>
      <span>· {fmtPct(r.taux_transfo)}</span>
      <span>· {fmtEur(r.don_moyen, 0)}</span>
      <span>· {r.age_moyen ? `${fmtNum(r.age_moyen, 0)} ans` : '—'}</span>
    </span>
  </div>
);

const SpotCard = ({ s, rank }) => {
  const elig = ELIG[s.eligibility] || ELIG.ok;
  const EligIcon = elig.icon;
  const blocked = s.eligibility !== 'ok';
  return (
    <div data-testid="booking-spot-card"
      className={`flex flex-col rounded-2xl border bg-card p-4 card-shadow ${blocked ? 'opacity-95' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground tabular">{rank}</span>
          <div className="min-w-0">
            <p className="break-words font-display text-[15px] font-semibold leading-tight text-foreground">{s.lieu}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground break-words"><MapPin className="h-3 w-3 shrink-0" />{s.region}</p>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${elig.cls}`} data-testid="booking-elig-badge">
          <EligIcon className="h-3 w-3" />{elig.label}
        </span>
      </div>

      {/* Opportunity index */}
      <div className="mt-3 flex items-center gap-3">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-2xl font-semibold tabular accent-text">{s.score}</span>
          <span className="text-[10px] text-muted-foreground">/100 indice</span>
        </div>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full" style={{ width: `${s.score}%`, background: 'var(--tenant-accent-500)' }} />
        </div>
      </div>

      {/* Metrics */}
      <div className="mt-3 grid grid-cols-5 gap-1.5 border-t pt-3">
        <Metric icon={Target} label="BS/j" value={fmtNum(s.avg_bs_rue, 1)} />
        <Metric icon={Percent} label="Transfo" value={fmtPct(s.taux_transfo)} />
        <Metric icon={Wallet} label="Don moy." value={fmtEur(s.don_moyen, 0)} />
        <Metric icon={Hourglass} label="Âge moy." value={s.age_moyen ? `${fmtNum(s.age_moyen, 0)} ans` : '—'} />
        <Metric icon={CalendarDays} label="Fiab." value={`${fmtInt(s.days)} j`} />
      </div>

      {/* Reco association + best days */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Building2 className="h-3 w-3" />Asso conseillée : <strong className="text-foreground">{s.reco_association_name}</strong>
        </span>
        {(s.best_days || []).length > 0 && (
          <span className="inline-flex flex-wrap items-center gap-1">
            {(s.best_days || []).map((d) => (
              <span key={d} className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{DAY_ABBR[d] || d}</span>
            ))}
          </span>
        )}
      </div>

      {/* Team + recency */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" />Effectif conseillé : <strong className="text-foreground">{s.team_suggested}</strong> <span className="text-muted-foreground">(plafond CNFF {s.team_cap})</span></span>
        <span>
          Dernier passage : {s.last_visit ? `il y a ${fmtNum(s.weeks_since_last, 0)} sem.` : 'jamais'}
        </span>
      </div>

      {/* Block reason */}
      {blocked && s.block_reason && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700" data-testid="booking-block-reason">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{s.block_reason}</span>
        </div>
      )}

      {/* Recommended recruiters */}
      {(s.recruiters || []).length > 0 && (
        <div className="mt-3 border-t pt-3">
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
            <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Sparkles className="h-3 w-3" />Recruteurs à positionner
              <span className="normal-case text-muted-foreground/70">· d'après {s.recruiters_source === 'région' ? 'la région' : 'ce spot'}</span>
            </p>
            <span className="text-[9px] tabular text-muted-foreground/70">BS/j · transfo · don · âge</span>
          </div>
          <div className="space-y-1.5">
            {(s.recruiters || []).map((r) => <RecruiterRow key={r.name} r={r} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export const BookingPlan = ({ scopeAssociations = [] }) => {
  const { body, options } = useFilters();
  const regions = options?.regions || [];
  const months = useMemo(buildMonths, []);
  const [canal, setCanal] = useState('Porte à Porte');
  const [region, setRegion] = useState(ALL);
  const [association, setAssociation] = useState(ALL);
  const [targetMonth, setTargetMonth] = useState(months[1]?.value || months[0]?.value);
  const [onlyEligible, setOnlyEligible] = useState(true);
  const [activeRecruitersOnly, setActiveRecruitersOnly] = useState(false);
  const [sortBy, setSortBy] = useState('score');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(24);
  const [exporting, setExporting] = useState(false);

  const reqBody = useMemo(() => ({
    ...body, association, canal, region, target_month: targetMonth,
    recruiters_active_only: activeRecruitersOnly, sort_by: sortBy,
  }), [body, association, canal, region, targetMonth, activeRecruitersOnly, sortBy]);
  const { data, isLoading, isError } = usePost('/admin/geo/booking-plan', reqBody);

  const spots = data?.spots || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return spots
      .filter((s) => (onlyEligible ? s.eligibility === 'ok' : true))
      .filter((s) => (q ? s.lieu.toLowerCase().includes(q) || (s.region || '').toLowerCase().includes(q) : true));
  }, [spots, onlyEligible, search]);
  const shown = filtered.slice(0, limit);

  const runExport = async () => {
    setExporting(true);
    try {
      await downloadPost('/admin/geo/booking-plan/excel', reqBody, `SHARITI_plan_booking_${targetMonth}.xlsx`);
      toast.success('Plan de booking exporté');
    } catch {
      toast.error('Export impossible');
    } finally {
      setExporting(false);
    }
  };

  const rules = data?.rules || {};

  return (
    <div className="space-y-5" data-testid="booking-plan">
      {/* Intro */}
      <div className="flex items-start gap-3 rounded-2xl border p-4 accent-bg-soft" data-testid="booking-intro">
        <Store className="mt-0.5 h-5 w-5 shrink-0 accent-text" />
        <p className="text-sm text-foreground">
          <span className="font-semibold">Où booker le mois prochain ?</span> Sélectionnez un canal et un mois : SHARITI classe les
          meilleurs spots à réserver (historique complet), vérifie la <span className="font-semibold">conformité CNFF</span>
          {' '}(délai de déclaration, jachère porte-à-porte, saisonnalité) et vous conseille l'association et les recruteurs à positionner.
        </p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 gap-3 rounded-2xl border bg-card p-4 card-shadow md:grid-cols-2 lg:grid-cols-3" data-testid="booking-controls">
        <div>
          <span className="mb-1 block text-[11px] text-muted-foreground">Canal</span>
          <Select value={canal} onValueChange={setCanal}>
            <SelectTrigger className="h-9 rounded-xl bg-card" data-testid="booking-canal"><SelectValue /></SelectTrigger>
            <SelectContent>{CANAUX.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="mb-1 block text-[11px] text-muted-foreground">Région</span>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="h-9 rounded-xl bg-card" data-testid="booking-region"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes les régions</SelectItem>
              {regions.map((rg) => <SelectItem key={rg} value={rg}>{rg}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <span className="mb-1 block text-[11px] text-muted-foreground">Mois visé</span>
          <Select value={targetMonth} onValueChange={setTargetMonth}>
            <SelectTrigger className="h-9 rounded-xl bg-card" data-testid="booking-month"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <span className="mb-1 block text-[11px] text-muted-foreground">Périmètre association</span>
          <Select value={association} onValueChange={setAssociation}>
            <SelectTrigger className="h-9 rounded-xl bg-card" data-testid="booking-association"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes — recommander la meilleure</SelectItem>
              {scopeAssociations.map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name || a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <span className="mb-1 block text-[11px] text-muted-foreground">Trier par</span>
          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setLimit(24); }}>
            <SelectTrigger className="h-9 rounded-xl bg-card" data-testid="booking-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button variant="outline" className="h-9 w-full gap-1.5 rounded-xl" onClick={runExport} disabled={exporting || isLoading || !spots.length} data-testid="booking-export">
            <FileSpreadsheet className="h-4 w-4" />{exporting ? 'Export…' : 'Exporter (Excel)'}
          </Button>
        </div>
      </div>

      {isError ? (
        <EmptyState title="Accès restreint" body="Cette analyse est réservée aux administrateurs SHARITI." />
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} height="h-52" />)}
        </div>
      ) : (
        <>
          {/* CNFF deadline banner */}
          <div className={`flex flex-col gap-2 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${data.deadline_ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/10'}`} data-testid="booking-deadline">
            <div className="flex items-start gap-3">
              {data.deadline_ok
                ? <CalendarCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />}
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {data.deadline_ok
                    ? `Déclaration CNFF possible jusqu'au ${fmtDate(data.deadline)}`
                    : `Délai CNFF dépassé pour ${data.target_month_label}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  Règle {canal} : dépôt du planning {rules.lead}.
                  {data.deadline_ok
                    ? ` Il reste ${fmtInt(data.days_to_deadline)} jour(s).`
                    : ` L'échéance était le ${fmtDate(data.deadline)} — visez un mois plus tardif.`}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-lg border bg-card px-2 py-1 text-muted-foreground"><CalendarClock className="h-3 w-3" />{rules.jachere}</span>
              <span className="inline-flex items-center gap-1 rounded-lg border bg-card px-2 py-1 text-muted-foreground"><Users2 className="h-3 w-3" />{rules.team}</span>
            </div>
          </div>

          {/* Summary + filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full accent-bg-soft accent-text px-3 py-1 font-medium" data-testid="booking-eligible-count">
                <ShieldCheck className="h-3.5 w-3.5" />{fmtInt(data.eligible_count)} spot(s) éligible(s)
              </span>
              <span className="text-muted-foreground">sur {fmtInt(data.count)} analysés · mois visé <strong className="text-foreground">{data.target_month_label}</strong></span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setOnlyEligible((v) => !v)} aria-pressed={onlyEligible} data-testid="booking-only-eligible"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)] ${onlyEligible ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700' : 'bg-card text-muted-foreground hover:border-[color:var(--tenant-accent-300)]'}`}>
                <ShieldCheck className="h-3.5 w-3.5" />Éligibles uniquement
              </button>
              <button type="button" onClick={() => setActiveRecruitersOnly((v) => !v)} aria-pressed={activeRecruitersOnly} data-testid="booking-active-recruiters"
                title="N'afficher que les recruteurs actifs (vivier) dans les recommandations"
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)] ${activeRecruitersOnly ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700' : 'bg-card text-muted-foreground hover:border-[color:var(--tenant-accent-300)]'}`}>
                <UserCheck className="h-3.5 w-3.5" />Recruteurs actifs
              </button>
              <div className="relative min-w-0 sm:w-56">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une ville…" className="rounded-xl pl-9" data-testid="booking-search" />
              </div>
            </div>
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <EmptyState title="Aucun spot" body="Aucun spot ne correspond aux filtres. Désactivez « Éligibles uniquement » pour voir aussi les spots bloqués (jachère, saison)." />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="booking-grid">
                {shown.map((s, i) => <SpotCard key={s.lieu} s={s} rank={i + 1} />)}
              </div>
              {shown.length < filtered.length && (
                <div className="flex justify-center">
                  <Button variant="outline" className="rounded-xl" onClick={() => setLimit((l) => l + 24)} data-testid="booking-load-more">
                    Afficher plus ({filtered.length - shown.length} restant(s))
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
